function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
var _globalThis, _Symbol_for, _;
import { uniqBy } from 'lodash';
import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { UserStatus, UserType } from 'api/models';
import { AssigneePickerFilter, AssigneePickerObjectTypes } from 'hooks/assignee-picker/types/types';
import { getSimpleSearchFilterModule } from 'utils/simple-search-filter/module';
const assigneePickerFilterToObjectTypeMap = {
    [AssigneePickerFilter.All]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.NativeUsersAndTeams]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.NativePartnerUsersAndTeams]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.UnResolvedPlaceholders]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.InProjectUsersAndTeams]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.InProjectUsers]: [
        AssigneePickerObjectTypes.InProject
    ],
    [AssigneePickerFilter.NotInProjectUsers]: [
        AssigneePickerObjectTypes.NotInProject
    ],
    [AssigneePickerFilter.Teams]: [
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.NotInProjectUsersAndTeams]: [
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Team
    ],
    [AssigneePickerFilter.ExternalUsers]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject
    ],
    [AssigneePickerFilter.ExternalAndClientPartnerUsers]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject
    ],
    [AssigneePickerFilter.NativeUsers]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject
    ],
    [AssigneePickerFilter.PlaceholderResolutions]: [
        AssigneePickerObjectTypes.InProject,
        AssigneePickerObjectTypes.NotInProject,
        AssigneePickerObjectTypes.Company,
        AssigneePickerObjectTypes.SameRole
    ]
};
export class MembersAPIClient extends HttpClient {
    async findAll() {
        return this.get('/members');
    }
    async getMembersV2() {
        return this.get('/members/projects');
    }
    async getTeams() {
        return this.get('/members/project-teams');
    }
    async getTeamsWithMembers() {
        return this.get('/members/projects/teams');
    }
    async inviteMembers(data) {
        return this.post('/members/bulk-action', data);
    }
    // public async addMember(data: AddMemberPayload): Promise<UserType> {
    //   return this.post('/members', data);
    // }
    async updateMember(data) {
        return this.put('/members', data);
    }
    async updateMemberWithFields(data) {
        return this.put('/members', data);
    }
    async updateExternalMembers(data) {
        return this.put(`/projects/${data.projectId}/external-members`, {
            user: {
                userId: data.user.userId
            },
            permissionSet: {
                permissionSetId: data.permissionSet.permissionSetId
            }
        });
    }
    async getMemberIntegrations() {
        return this.get('/members/integrations');
    }
    async updateMemberIntegrations(data) {
        return this.put('/members/integrations', data);
    }
    constructor(...args){
        super(...args), _define_property(this, "getAllProjectMemberships", async ()=>this.get('/projects/get-members')), _define_property(this, "exportAsCSV", async (filters, match, userType = UserType.Native, userStatus = UserStatus.Active)=>{
            const { ApiFilter } = await getSimpleSearchFilterModule();
            return this.get(`/members/export?${stringify({
                userStatus
            })}&${stringify({
                userType
            })}&${ApiFilter.toQueryStringFormat(filters)}&${stringify({
                match: filters.length ? match : null
            }, {
                skipNull: true
            })}`).then((response)=>response.downloadUrl);
        }), _define_property(this, "fetchPaginatedMembersData", async ({ groupId, projectId = -1, searchString = '', fieldFilters, isPrivate = false, includeRestricedPartnersMembers = false, taskStartDateTime, taskEndDateTime, userStatus, userType, fetchUnResolvedPlaceholdersOnly, assigneePickerFilter = 'ALL', startRow = 0, endRow = 30, includePermissions = false, includeTimeSheetApprovers = false, includeFields = false, includedUserIds, includedTeamIds, companyId })=>{
            var _ref, _ref1, _ref2, _ref3, _ref4, _ref5, _ref6, _ref7;
            const defaultParams = {
                query: searchString,
                offset: startRow,
                limit: endRow - startRow
            };
            if (includedUserIds === null || includedUserIds === void 0 ? void 0 : includedUserIds.length) {
                defaultParams.includedUserIds = includedUserIds.filter(Number.isFinite).join(',');
            }
            if (includedTeamIds === null || includedTeamIds === void 0 ? void 0 : includedTeamIds.length) {
                defaultParams.includedTeamIds = includedTeamIds.filter(Number.isFinite).join(',');
            }
            if (fieldFilters === null || fieldFilters === void 0 ? void 0 : fieldFilters.length) {
                const { ApiGroupFilter } = await getSimpleSearchFilterModule();
                defaultParams.fieldFilters = ApiGroupFilter.toQueryStringFormatForGroups(fieldFilters);
            }
            if (fetchUnResolvedPlaceholdersOnly) {
                defaultParams.isResolved = false;
            }
            if (projectId && projectId > 0) {
                defaultParams.projectId = projectId;
            }
            if (isPrivate) {
                defaultParams.isPrivate = true;
            }
            if (includeRestricedPartnersMembers) {
                defaultParams.includeRestricedPartnersMembers = true;
            }
            if (taskStartDateTime && taskEndDateTime) {
                defaultParams.startDateTime = taskStartDateTime;
                defaultParams.endDateTime = taskEndDateTime;
            }
            if (userStatus && userStatus.length > 0) {
                defaultParams.userStatus = userStatus;
            }
            if (userType && userType.length > 0) {
                defaultParams.userType = userType;
            }
            if (groupId) {
                defaultParams.groupId = groupId;
            }
            if (companyId) {
                defaultParams.companyId = companyId;
            }
            // Hadle case where assignee picker filter is set to but user type is not set.
            if (assigneePickerFilter && !(userType === null || userType === void 0 ? void 0 : userType.length)) {
                if (assigneePickerFilter === 'NATIVE_USERS') {
                    defaultParams.userType = [
                        UserType.Native
                    ];
                } else if (assigneePickerFilter === 'NATIVE_PARTNER_USERS_AND_TEAMS') {
                    defaultParams.userType = [
                        UserType.Native,
                        UserType.Partner,
                        UserType.CustomerPortalBot
                    ];
                } else if (assigneePickerFilter === 'EXTERNAL_USERS') {
                    defaultParams.userType = [
                        UserType.External
                    ];
                } else if (assigneePickerFilter === 'EXTERNAL_AND_CLIENT_PARTNER_USERS') {
                    defaultParams.userType = [
                        UserType.External,
                        UserType.CustomerPartner
                    ];
                }
            }
            if (assigneePickerFilter) {
                defaultParams.objectTypes = assigneePickerFilterToObjectTypeMap[assigneePickerFilter];
            }
            if (includePermissions) {
                defaultParams.includePermissions = true;
            }
            if (includeTimeSheetApprovers) {
                defaultParams.includeTimeSheetApprovers = true;
            }
            if (includeFields) {
                defaultParams.includeFields = true;
            }
            const url = `/users/search?${stringify(defaultParams)}`;
            const response = await this.get(url);
            const isRealUser = (u)=>{
                var _u_emailId;
                return (_u_emailId = u.emailId) === null || _u_emailId === void 0 ? void 0 : _u_emailId.includes('@');
            };
            return {
                inProjectUsers: ((_ref = response === null || response === void 0 ? void 0 : response.inProjectList) !== null && _ref !== void 0 ? _ref : []).filter(isRealUser),
                notInProjectUsers: ((_ref1 = response === null || response === void 0 ? void 0 : response.notInProjectList) !== null && _ref1 !== void 0 ? _ref1 : []).filter(isRealUser),
                teams: uniqBy((_ref2 = response === null || response === void 0 ? void 0 : response.teams) !== null && _ref2 !== void 0 ? _ref2 : [], 'teamId'),
                companies: (_ref3 = response === null || response === void 0 ? void 0 : response.companyList) !== null && _ref3 !== void 0 ? _ref3 : [],
                limit: (_ref4 = response === null || response === void 0 ? void 0 : response.limit) !== null && _ref4 !== void 0 ? _ref4 : 0,
                offset: (_ref5 = response === null || response === void 0 ? void 0 : response.offset) !== null && _ref5 !== void 0 ? _ref5 : 0,
                includedUsers: ((_ref6 = response === null || response === void 0 ? void 0 : response.includedUsers) !== null && _ref6 !== void 0 ? _ref6 : []).filter(isRealUser),
                includedTeams: uniqBy((_ref7 = response === null || response === void 0 ? void 0 : response.includedTeams) !== null && _ref7 !== void 0 ? _ref7 : [], 'teamId')
            };
        }), _define_property(this, "fetchUnresolvedPlaceholders", async ({ projectId, searchString = '', fetchUnResolvedPlaceholdersOnly = false, assigneePickerFilter })=>{
            var _ref, _ref1, _ref2;
            const defaultParams = {
                offset: 0,
                limit: 30,
                isResolved: fetchUnResolvedPlaceholdersOnly ? false : undefined
            };
            if (searchString) {
                defaultParams.query = searchString;
            }
            if (projectId && projectId > 0) {
                defaultParams.projectId = projectId;
            }
            if (assigneePickerFilter) {
                defaultParams.objectTypes = assigneePickerFilterToObjectTypeMap[assigneePickerFilter];
            }
            const url = `/teams/search?${stringify(defaultParams)}`;
            const response = await this.get(url);
            return {
                inProjectUsers: [],
                notInProjectUsers: [],
                teams: (_ref = response === null || response === void 0 ? void 0 : response.teams) !== null && _ref !== void 0 ? _ref : [],
                companies: [],
                limit: (_ref1 = response === null || response === void 0 ? void 0 : response.limit) !== null && _ref1 !== void 0 ? _ref1 : 0,
                offset: (_ref2 = response === null || response === void 0 ? void 0 : response.offset) !== null && _ref2 !== void 0 ? _ref2 : 0
            };
        }), _define_property(this, "exportSkillsAsCSV", async ({ filters, match, columnOrder })=>{
            const { ApiGroupFilter } = await getSimpleSearchFilterModule();
            const url = `/members/skills/export?${ApiGroupFilter.toQueryStringFormatForGroups(filters)}&${stringify({
                match: filters.length ? match : null
            }, {
                skipNull: true
            })}`;
            return this.post(url, {
                columnOrder
            });
        }), _define_property(this, "getProjectsFinancialBudgets", async (projectIds)=>{
            const url = (projectIds === null || projectIds === void 0 ? void 0 : projectIds.length) ? `/members/projects/financial-budgets?projectIds=${projectIds.join(',')}` : '/members/projects/financial-budgets';
            return this.get(url);
        });
    }
}
export const MembersAPI = process.env.VITEST ? (_ = (_globalThis = globalThis)[_Symbol_for = Symbol.for('rocketlane:MembersAPI')]) !== null && _ !== void 0 ? _ : _globalThis[_Symbol_for] = new MembersAPIClient() : new MembersAPIClient();
export * from './types';
